import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as Parse from 'parse';
import { AdminCommunityGroupSummary, AdminGroupListFilter, CatalogueItem, CommunityGroupDetail, CommunityGroupSummary, GroupListFilter, MyGroupsTab, Page } from 'backend/interfaces/community-groups';

export interface CommunityConfiguration { enabled: boolean; writesEnabled: boolean; categoryIds: { golf?: string; cycling?: string }; }
export interface MemberRow { id: string; userId: string; name: string; surname: string; role?: string; status: string; activity?: { handicapIndex?: number; experienceLevel?: string; disciplines?: string[]; terrains?: string[] }; }

@Injectable({ providedIn: 'root' })
export class CommunityGroupService {
  readonly groupCreated$ = new Subject<void>();
  configuration(): Promise<CommunityConfiguration> {
    return Parse.Cloud.run('communityGroups.configuration');
  }
  list(filters: GroupListFilter): Promise<Page<CommunityGroupSummary>> {
    return Parse.Cloud.run('communityGroups.list', filters);
  }
  adminList(filters: AdminGroupListFilter): Promise<Page<AdminCommunityGroupSummary>> {
    return Parse.Cloud.run('communityGroups.adminList', filters);
  }
  get(id: string): Promise<CommunityGroupDetail> {
    return Parse.Cloud.run('communityGroups.get', { id });
  }
  mine(tab: MyGroupsTab, cursor?: string): Promise<Page<CommunityGroupSummary>> {
    return Parse.Cloud.run('communityGroups.myGroups', { tab, cursor });
  }
  catalogue(kind: 'areas' | 'courses', name = '', cursor?: string): Promise<Page<CatalogueItem>> {
    return Parse.Cloud.run('communityGroups.catalogue', { kind, name, cursor });
  }
  members(groupId: string, requests = false, cursor?: string): Promise<Page<MemberRow>> {
    return Parse.Cloud.run(requests ? 'communityGroups.listRequests' : 'communityGroups.members', { groupId, cursor });
  }
  invitations(groupId: string): Promise<Page<{ id: string; recipientPhone: string; status: string; deliveryStatus: string }>> {
    return Parse.Cloud.run('communityGroups.listInvitations', { groupId });
  }
  resolveInvitation(token: string): Promise<{ groupId: string; groupName: string; returnPath: string }> {
    return Parse.Cloud.run('communityGroups.resolveInvitation', { token });
  }
  signedIn(): boolean { return !!Parse.User.current(); }
  /** Callers retain this key across failures and discard it only after a successful mutation. */
  operationKey(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  mutate<T = any>(method: string, params: object, operationKey: string): Promise<T> {
    return Parse.Cloud.run(`communityGroups.${method}`, { ...params, operationKey });
  }
  async uploadLogo(file: File): Promise<{ name: string; url: string }> {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) throw new Error('Choose a PNG, JPEG or WebP image under 5 MB.');
    const image = await createImageBitmap(file);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 512;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Image processing is unavailable in this browser.');
      const size = Math.min(image.width, image.height);
      context.drawImage(image, (image.width - size) / 2, (image.height - size) / 2, size, size, 0, 0, 512, 512);
      const uploaded = await new Parse.File('community-logo.png', { base64: canvas.toDataURL('image/png').split(',')[1] }).save();
      return { name: uploaded.name(), url: uploaded.url() };
    } finally { image.close(); }
  }
}
